import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { llmReasoningApi, type BacktestSummary } from '../api/client'
import './LLMBacktestZone.css'

const SEV: Record<string, string> = { ok: '🟢', warn: '🟠', bad: '🔴' }
const REC_SEV: Record<string, string> = { critical: '🔴', warn: '🟠', info: '🔵', ok: '🟢' }

export function LLMBacktestZone() {
  const q = useQuery({
    queryKey: ['llm-backtest-summary'],
    queryFn: llmReasoningApi.backtestSummary,
    refetchInterval: 30000,
  })

  if (q.isLoading) return <Card><p className="bt-muted">Loading backtest…</p></Card>
  if (q.isError) return <Card><p className="bt-muted">Failed to load backtest summary.</p></Card>
  const d = q.data as BacktestSummary
  if (!d || d.run === null || !d.progress || !d.impact) {
    return <Card><p className="bt-muted">No backtest runs recorded yet.</p></Card>
  }

  const p = d.progress, im = d.impact
  return (
    <div className="bt-zone">
      <div className="bt-head">
        <code>{p.run_id}</code>
        <span className={`bt-pill bt-${p.status}`}>
          {p.status === 'running' ? '● running' : '✓ completed'}
        </span>
      </div>

      <div className="dash-zone-grid dash-zone-grid--2">
        <div className="card bt-tile">
          <h3>① Progress</h3>
          <div className="bt-big">{p.cycles} <span className="bt-muted">cycles</span></div>
          <Row k="Spend" v={`$${p.spend_usd.toFixed(2)} ($${p.avg_cost_usd.toFixed(3)}/cyc)`} />
          <Row k="Avg latency" v={`${(p.avg_latency_ms / 1000).toFixed(0)}s`} />
          <Row k="Last cycle" v={p.last_cycle_at ? new Date(p.last_cycle_at).toLocaleString() : '—'} />
        </div>

        <div className="card bt-tile">
          <h3>② Impact</h3>
          <Row k="Apply rate" v={`${(im.apply_rate * 100).toFixed(1)}% (${im.applied} applied)`} />
          <Row k="Proposals" v={`${im.proposals_total} · ${im.applied}/${im.queued}/${im.rejected}`} />
          <Row k="Envelope bypasses" v={im.envelope_bypasses === 0 ? '0 ✓' : `${im.envelope_bypasses} ✗`} />
          <Row k="Hallucination" v={`${(im.hallucination_rate * 100).toFixed(1)}%`} />
          <Row k="Theses/cycle" v={`${im.avg_theses_per_cycle.toFixed(0)}`} />
        </div>
      </div>

      <div className="card bt-tile bt-full">
        <h3>③ Learnings — what to change next run</h3>
        <div className="bt-list-body">
          {d.learnings.map((l) => (
            <p key={l.key} className="bt-learn">{SEV[l.severity] ?? '•'} {l.text}</p>
          ))}
        </div>
      </div>

      <div className="card bt-tile bt-full">
        <h3>④ Goal-Influence Delta</h3>
        <div className="bt-list-body">
          {d.goal_delta.length === 0 && (
            <p className="bt-muted">No goal adjustments applied in this run yet.</p>
          )}
          {d.goal_delta.map((g) => (
            <p key={g.goal_type} className="bt-delta">
              <strong>{g.goal_type}</strong>{' '}
              <span className="bt-muted">baseline</span> {g.baseline_value.toFixed(3)}{' '}
              <span className="bt-arrow">→</span> {g.adjusted_value.toFixed(3)}{' '}
              <span className="bt-muted">
                ({g.direction === 'down' ? '↓ more conservative' : g.direction === 'up' ? '↑ looser' : 'flat'},{' '}
                {(g.pct_change * 100).toFixed(1)}%, {g.applied_count} applied)
              </span>
            </p>
          ))}
        </div>
        <p className="bt-soon">
          📈 Projected P&amp;L impact — reserved for a future measured counterfactual.
        </p>
      </div>

      {/* ⑤ Gate verdict + ⑥ Per-fold + ⑦ Queue reasons : 3-col row */}
      <div className="dash-zone-grid dash-zone-grid--3">
        <div className="card bt-tile">
          <h3>⑤ Gate verdict</h3>
          <div className="bt-list-body">
            {(!d.gate || d.gate.length === 0) && <p className="bt-muted">No gate verdict.</p>}
            {d.gate?.map((g) => (
              <div key={g.strategy} className="bt-gate">
                <div className="bt-row">
                  <strong>{g.strategy}</strong>
                  <span className={`bt-pill ${g.passed ? 'bt-completed' : 'bt-fail'}`}>
                    {g.passed ? '✓ PASS' : '✗ FAIL'}
                  </span>
                </div>
                <div className="bt-chips">
                  {g.criteria.map((c) => (
                    <span key={c.name} className={`bt-chip ${c.passed ? 'bt-chip-ok' : 'bt-chip-bad'}`}>
                      {c.name} {c.value.toFixed(3)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card bt-tile">
          <h3>⑥ Per-fold Sharpe</h3>
          <div className="bt-list-body">
            {(!d.per_fold || d.per_fold.length === 0) && <p className="bt-muted">No folds.</p>}
            {d.per_fold?.map((pf) => (
              <div key={pf.strategy} className="bt-fold">
                <div className="bt-muted">{pf.strategy}</div>
                <div className="bt-spark">
                  {pf.sharpes.map((s: number, i: number) => (
                    <span key={i} className={`bt-bar ${s >= 0 ? 'bt-bar-pos' : 'bt-bar-neg'}`}
                          title={s.toFixed(2)}>{s.toFixed(2)}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card bt-tile">
          <h3>⑦ Queue reasons</h3>
          <div className="bt-list-body">
            {(!d.queue_reasons || d.queue_reasons.length === 0) && <p className="bt-muted">None queued.</p>}
            {d.queue_reasons?.map((q) => (
              <Row key={q.reason} k={q.reason} v={String(q.count)} />
            ))}
          </div>
        </div>
      </div>

      {/* ⑧ Recommendations : full width */}
      <div className="card bt-tile bt-full">
        <h3>⑧ Recommendations — adjustments to test</h3>
        <div className="bt-list-body">
          {(!d.recommendations || d.recommendations.length === 0) && (
            <p className="bt-muted">No recommendations.</p>
          )}
          {d.recommendations?.map((r) => {
            const sc = r.suggested_change
            return (
              <div key={r.key} className="bt-rec">
                <p className="bt-rec-head">{REC_SEV[r.severity] ?? '•'} <strong>{r.title}</strong></p>
                <p className="bt-rec-detail">{r.detail}</p>
                {sc && (
                  <button
                    type="button"
                    className="bt-copy"
                    onClick={() => navigator.clipboard?.writeText(`${sc.param} ${sc.value}`)}
                    title="Copy to clipboard"
                  >
                    <code>{sc.param} {sc.value}</code> ⧉
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function Card({ children }: { children: ReactNode }) {
  return <div className="card bt-tile">{children}</div>
}
function Row({ k, v }: { k: string; v: string }) {
  return <div className="bt-row"><span className="bt-muted">{k}</span><span>{v}</span></div>
}
